import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import {
  FlaskConical, CheckCircle2, Plus, X, Loader2, ClipboardCheck, AlertTriangle,
} from "lucide-react";
import EmptyState from "@/components/EmptyState";

const TABS = [
  { k: "", label: "All" },
  { k: "overdue", label: "Overdue" },
  { k: "due", label: "Due" },
  { k: "pending_review", label: "Pending Review" },
  { k: "reviewed", label: "Reviewed" },
];

const canEnter = (r) => ["owner", "provider", "nurse"].includes(r);
const canReview = (r) => ["owner", "provider"].includes(r);

function ResultsModal({ lab, onClose, onSaved }) {
  const [rows, setRows] = useState([{ marker: "", value: "", unit: "", ref_range: "", flag: "normal" }]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const update = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows([...rows, { marker: "", value: "", unit: "", ref_range: "", flag: "normal" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post(`/labs/${lab.id}/results`, { results: rows.filter((r) => r.marker && r.value), notes });
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="results-modal">
      <div className="bg-card rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
          <div>
            <h3 className="font-heading text-lg font-semibold">Enter Results</h3>
            <p className="text-xs text-muted-foreground">{lab.panel_name} · {lab.patient_name}</p>
          </div>
          <button onClick={onClose} data-testid="results-modal-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <style>{`.cell{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));border-radius:0.375rem;padding:0.4rem 0.5rem;font-size:0.8125rem;outline:none}.cell:focus{box-shadow:0 0 0 2px hsl(var(--ring))}`}</style>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`result-row-${i}`}>
              <input className="cell col-span-4" placeholder="Marker" value={r.marker} onChange={(e) => update(i, "marker", e.target.value)} />
              <input className="cell col-span-2" placeholder="Value" value={r.value} onChange={(e) => update(i, "value", e.target.value)} />
              <input className="cell col-span-2" placeholder="Unit" value={r.unit} onChange={(e) => update(i, "unit", e.target.value)} />
              <input className="cell col-span-2" placeholder="Ref" value={r.ref_range} onChange={(e) => update(i, "ref_range", e.target.value)} />
              <select className="cell col-span-1" value={r.flag} onChange={(e) => update(i, "flag", e.target.value)}>
                <option value="normal">Norm</option><option value="high">High</option><option value="low">Low</option>
              </select>
              <button type="button" onClick={() => removeRow(i)} className="col-span-1 text-muted-foreground hover:text-red-600 flex justify-center"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" onClick={addRow} data-testid="add-result-row" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add marker</button>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" rows={2} className="cell w-full" data-testid="results-notes" />
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-secondary">Cancel</button>
            <button data-testid="save-results-button" type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save results
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Labs() {
  const [labs, setLabs] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [resultsLab, setResultsLab] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/labs", { params: filter ? { status: filter } : {} });
    setLabs(data);
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const review = async (lab) => {
    setReviewing(lab.id);
    try {
      await api.post(`/labs/${lab.id}/review`);
      await load();
    } catch (e) {
      // ignore
    }
    setReviewing(null);
  };

  return (
    <div className="space-y-6" data-testid="labs-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Lab Review Queue</h1>
        <p className="text-sm text-muted-foreground mt-1">Track outstanding labs and sign off on results.</p>
      </div>

      <div className="flex gap-1 flex-wrap" data-testid="lab-filter-tabs">
        {TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`lab-tab-${t.k || "all"}`}
            onClick={() => setFilter(t.k)}
            className={`px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${filter === t.k ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50">
              <th className="text-left font-medium text-muted-foreground px-4 py-3">Patient</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3">Panel</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3 hidden md:table-cell">Due</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3">Status</th>
              <th className="text-right font-medium text-muted-foreground px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="labs-table-body">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  <td className="px-4 py-3.5"><div className="h-4 w-36 bg-secondary rounded animate-pulse" /></td>
                  <td className="px-4 py-3.5"><div className="h-4 w-32 bg-secondary rounded animate-pulse" /></td>
                  <td className="px-4 py-3.5 hidden md:table-cell"><div className="h-4 w-20 bg-secondary rounded animate-pulse" /></td>
                  <td className="px-4 py-3.5"><div className="h-5 w-16 bg-secondary rounded-full animate-pulse" /></td>
                  <td className="px-4 py-3.5"><div className="h-7 w-24 bg-secondary rounded-md animate-pulse ml-auto" /></td>
                </tr>
              ))
            ) : labs.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    testid="labs-empty"
                    icon={FlaskConical}
                    title="Nothing in this view"
                    description={filter ? "No labs with this status right now." : "Labs you order will appear here, organized by status."}
                  />
                </td>
              </tr>
            ) : (
              labs.map((lab) => (
                <tr key={lab.id} data-testid={`lab-row-${lab.id}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                  <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => navigate(`/patients/${lab.patient_id}`)}>{lab.patient_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{lab.panel_name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground tabular-nums">{lab.due_date?.slice(0, 10)}</td>
                  <td className="px-4 py-3"><StatusBadge status={lab.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {(lab.status === "due" || lab.status === "overdue") && canEnter(user.role) && (
                        <button data-testid={`enter-results-${lab.id}`} onClick={() => setResultsLab(lab)} className="inline-flex items-center gap-1.5 text-xs font-medium border border-border rounded-md px-2.5 py-1.5 hover:bg-secondary">
                          <ClipboardCheck className="w-3.5 h-3.5" /> Enter Results
                        </button>
                      )}
                      {lab.status === "pending_review" && canReview(user.role) && (
                        <button data-testid={`review-lab-${lab.id}`} onClick={() => review(lab)} disabled={reviewing === lab.id} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded-md px-2.5 py-1.5 hover:bg-primary/90 disabled:opacity-60">
                          {reviewing === lab.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Sign Off
                        </button>
                      )}
                      {lab.status === "pending_review" && !canReview(user.role) && (
                        <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Provider review</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {resultsLab && <ResultsModal lab={resultsLab} onClose={() => setResultsLab(null)} onSaved={() => { setResultsLab(null); load(); }} />}
    </div>
  );
}
